import { receiptsApi } from './receiptsApi';
import { isAmbiguousReceiptRequestError } from './receiptsErrors';
import type { LinkReceiptToTransactionPayload, Receipt } from './receiptsTypes';

type ReceiptLinkApi = Pick<typeof receiptsApi, 'fetchById' | 'link'>;

const assertConfirmedLink = (receipt: Receipt, transactionId: string): Receipt => {
  if (receipt.transactionId !== transactionId) {
    throw new Error('Receipt link response did not confirm the requested transaction.');
  }
  return receipt;
};

/** Only a 200 link response with the expected transaction id confirms both service writes. */
export const linkReceiptWithRecovery = async (
  payload: LinkReceiptToTransactionPayload,
  api: ReceiptLinkApi = receiptsApi,
  isSessionValid: () => boolean = () => true,
): Promise<Receipt> => {
  const assertSessionValid = (): void => {
    if (!isSessionValid()) throw new Error('Receipt link belongs to a previous session.');
  };

  assertSessionValid();
  try {
    const linkedReceipt = await api.link(payload);
    assertSessionValid();
    return assertConfirmedLink(linkedReceipt, payload.transactionId);
  } catch (error) {
    if (!isAmbiguousReceiptRequestError(error)) {
      throw error;
    }

    assertSessionValid();
    const receipt = await api.fetchById(payload.id);
    assertSessionValid();
    if (receipt.transactionId && receipt.transactionId !== payload.transactionId) {
      throw new Error('Receipt is linked to a different transaction.');
    }

    // A timed-out link may have completed only one of its two service writes. Even if the receipt
    // already names the target, retry the idempotent call to confirm the attachment is present.
    const linkedReceipt = await api.link(payload);
    assertSessionValid();
    return assertConfirmedLink(linkedReceipt, payload.transactionId);
  }
};
